import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Company.BillingStatus. null = not billed through sign-up (companies from before self sign-up). */
export type BillingStatus = 'Incomplete' | 'Trialing' | 'Active' | 'PastDue' | 'Canceled' | null;

export interface BillingPlan {
  key: string;
  name: string;
  monthly: number;
  yearly: number;
  maxDrivers: number;
  maxFacilities: number | null;
  assistantQuestions: number;
  available: { month: boolean; year: boolean };
}

export interface BillingCatalog {
  enabled: boolean;
  testMode: boolean;
  trialDays: number;
  setupFee?: { enabled: boolean; amount: number };
  currency: string;
  plans: BillingPlan[];
  driverRanges: { range: string; plan: string }[];
  carriers: string[];
}

export interface CompanyBilling {
  companyName: string | null;
  managed: boolean;
  status: BillingStatus;
  canUse: boolean;
  needsCheckout: boolean;
  plan: string | null;
  planName: string | null;
  interval: 'month' | 'year' | null;
  price: number | null;
  maxDrivers: number | null;
  trialEndsAt: string | null;
  hasBillingAccount: boolean;
  activeDrivers: number;
  billingEnabled: boolean;
  setupFeeDue?: number;
  driverCountRange: string | null;
  suggestedPlan: string | null;
}

export interface SetupStep {
  key: string;
  title: string;
  done: boolean;
  optional: boolean;
  detail: string;
  link: string;
}

export interface SetupStatus {
  companyId: number;
  companyName: string;
  logoUrl?: string | null;
  brandColor?: string | null;
  joinCode?: string | null;
  billingStatus: BillingStatus;
  trialEndsAt: string | null;
  /** Signed up by itself (accepted the terms at sign-up): guide and step-by-step menu even with billing off. */
  selfServe?: boolean;
  counts: {
    facilities: number; managers: number; drivers: number; zones: number; zonesWithPay: number; templates: number;
    withLocation?: number; withStation?: number; applicants?: number;
  };
  steps: SetupStep[];
}

export interface CreateCompanyResult {
  message: string;
  company: { id: number; name: string; email: string; address: string; phoneNumber: string; websiteUrl: string | null; logoUrl: string | null };
  billing: { enabled: boolean; status: BillingStatus; suggestedPlan: string | null };
  token: string;
}

export interface JoinLink {
  code: string;
  link: string | null;
  facilities: { id: number; name: string; link: string | null }[];
}

export interface JoinInfo {
  companyName: string;
  logoUrl: string | null;
  brandColor: string | null;
  facilities: { id: number; name: string }[];
}

export interface JoinRequest {
  code: string;
  warehouseId: number | null;
  name: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  language: string;
  acceptsSms: boolean;
}

/** Statuses that still need the plan and payment steps. */
export function needsCheckout(status: BillingStatus | string | null | undefined): boolean {
  return status === 'Incomplete' || status === 'Canceled';
}

/**
 * The registration endpoints: plans, Stripe's payment page, coming back from it, Stripe's billing page, setup checklist,
 * the driver join link and the public join page.
 */
@Injectable({ providedIn: 'root' })
export class BillingService {
  private api = environment.apiUrl;

  constructor(private http: HttpClient) { }

  plans(): Observable<BillingCatalog> {
    return this.http.get<BillingCatalog>(`${this.api}/Billing/plans`);
  }

  status(): Observable<CompanyBilling> {
    return this.http.get<CompanyBilling>(`${this.api}/Billing/status`);
  }

  checkout(plan: string, interval: 'month' | 'year'): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.api}/Billing/checkout`, { plan, interval });
  }

  confirm(sessionId: string): Observable<CompanyBilling> {
    return this.http.post<CompanyBilling>(`${this.api}/Billing/confirm`, { sessionId });
  }

  portal(): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(`${this.api}/Billing/portal`, {});
  }

  setupStatus(): Observable<SetupStatus> {
    return this.http.get<SetupStatus>(`${this.api}/Companies/setup-status`);
  }

  createCompany(form: FormData): Observable<CreateCompanyResult> {
    return this.http.post<CreateCompanyResult>(`${this.api}/Companies/create`, form);
  }

  joinLink(): Observable<JoinLink> {
    return this.http.get<JoinLink>(`${this.api}/Companies/join-link`);
  }

  newJoinLink(): Observable<JoinLink> {
    return this.http.post<JoinLink>(`${this.api}/Companies/join-link/new`, {});
  }

  joinInfo(code: string): Observable<JoinInfo> {
    return this.http.get<JoinInfo>(`${this.api}/Join/${encodeURIComponent(code)}`);
  }

  join(body: JoinRequest): Observable<{ message: string; email: string; companyName: string }> {
    return this.http.post<{ message: string; email: string; companyName: string }>(`${this.api}/Join`, body);
  }

  /** Saves what the dashboard needs after the company or its billing changes (token, company flag, billing status). */
  remember(token: string | null, billingStatus: BillingStatus | undefined): void {
    try {
      if (token) localStorage.setItem('token', token);
      localStorage.setItem('hasCompany', 'true');
      if (billingStatus === undefined) return;
      if (billingStatus) localStorage.setItem('billingStatus', billingStatus);
      else localStorage.removeItem('billingStatus');
    } catch { /* storage blocked: the next login fills it */ }
  }
}
